//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { Box, Button, Grid, InputLabel, TextField, Typography } from '@mui/material'
import React, { ReactElement, useEffect, useState } from 'react'

import { theme } from '@react/components/theme'

import { TableHeaderItem } from '@react/components/TableHeaderItem'
import { useOtChainState, useOtChainTimestamp, useOtDispatch, useOtSelector } from '@react/redux/store'
import { lytPoolExchangeRateSetTx } from '@react/signedTxs/lytPoolSetDynamicExchangeRate'
import {
  dayNumberToDateString,
  erc20WeiToCommaDecString,
  exchangeRateDecimalStringToExchangeRate,
  exchangeRateToDecimalString,
  toUTCDateString,
} from '@shared/conversions'
import { EXCHANGE_RATE_ONE } from '@shared/Enums'
import { logOT } from '@shared/loggingShared'
import { LytExchangeRateCheckCTO } from '@shared/LytExchangeRateCheckCTO'
import { PoolLytOverviewCTO } from '@shared/PoolLytOverviewCTO'
import { EthAddr } from '@shared/SchemaTypes'
import { fetchLytExchangeRateCheckCTO } from './LytFetchers'

export default function LytSetDynamicExchangeRateForm(props: { poolAddr: EthAddr }): ReactElement {
  const { poolAddr } = props
  const {
    stageConfiguration: { serverApiBaseUrl, restAPIKey, lytPoolAddr },
  } = useOtChainState()
  const [_lytExchangeRateCheckCTO, setLytExchangeRateCheckCTO] = useState<LytExchangeRateCheckCTO>()
  useEffect(() => {
    if (lytPoolAddr) {
      fetchLytExchangeRateCheckCTO(serverApiBaseUrl, restAPIKey, lytPoolAddr)
        .then((_lytExchangeRateCheckCTO) => {
          setLytExchangeRateCheckCTO(_lytExchangeRateCheckCTO)
          setExchangeRate(_lytExchangeRateCheckCTO.expectedExchangeRate)
          setExchangeRateDecimalString(exchangeRateToDecimalString(_lytExchangeRateCheckCTO.expectedExchangeRate))
        })
        .catch((error) => {
          logOT.error('Error fetching lyt exchange rate check cto:', error)
        })
    }
  }, [lytPoolAddr])
  const poolCTO = useOtSelector((state) => state.poolState.poolOverviewCTOs[poolAddr] as PoolLytOverviewCTO)
  const [exchangeRate, setExchangeRate] = React.useState<string>(poolCTO.exchangeRate)
  const [exchangeRateDecimalString, setExchangeRateDecimalString] = React.useState<string>(
    exchangeRateToDecimalString(poolCTO.exchangeRate)
  )

  const chainTimestamp = useOtChainTimestamp()
  const dispatch = useOtDispatch()

  const [errorAmount, _setErrorAmount] = React.useState<string | undefined>(undefined)

  const [isSubmitted, setIsSubmitted] = React.useState(false)

  const sx = {
    backgroundColor: theme.palette.fg.main,
    color: '#FFFFFF',
    padding: '11px 16px 11px 16px',
    marginLeft: '8px',
    borderRadius: '6px',
    marginTop: 2,
  }

  const setExchangeRateAction = (): void => {
    setIsSubmitted(true)
    dispatch(
      lytPoolExchangeRateSetTx({
        poolAddr,
        exchangeRate,
      })
    )
    const event = new Event('modalClose')
    window.dispatchEvent(event)
  }
  const disableButton = false

  const setDatAssets = (BigInt(poolCTO.exchangeRateAtSetDay) * BigInt(poolCTO.totalShares)) / EXCHANGE_RATE_ONE

  return (
    <Grid container sx={{ marginTop: 2 }}>
      <Grid container spacing={2}>
        <Grid item xs={3}>
          <TableHeaderItem>Assets</TableHeaderItem>
        </Grid>
        <Grid item xs={3}>
          <TableHeaderItem>Shares</TableHeaderItem>
        </Grid>
        <Grid item xs={2}>
          <TableHeaderItem>Date</TableHeaderItem>
        </Grid>
        <Grid item xs={4}>
          <TableHeaderItem>Exchange Rate</TableHeaderItem>
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TableHeaderItem>At Last Exchange rate update</TableHeaderItem>
        </Grid>
      </Grid>

      <Grid container sx={{ marginBottom: 4 }}>
        <Grid item xs={3}>
          <Box sx={{ width: '75%' }}>{erc20WeiToCommaDecString(setDatAssets)} </Box>
        </Grid>
        <Grid item xs={3}>
          <Typography sx={{ marginLeft: 1 }} variant="bodyBold">
            {erc20WeiToCommaDecString(poolCTO.totalShares)}
          </Typography>
        </Grid>
        <Grid item xs={2}>
          <Typography sx={{ marginLeft: 1 }} variant="bodyBold">
            {dayNumberToDateString(poolCTO.exchangeRateSetDay)}
          </Typography>
        </Grid>
        <Grid item xs={4}>
          <Typography sx={{ marginLeft: 1 }} variant="bodyBold">
            {exchangeRateToDecimalString(poolCTO.exchangeRateAtSetDay)}
          </Typography>
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TableHeaderItem>Current values</TableHeaderItem>
        </Grid>
      </Grid>

      <Grid container sx={{ marginBottom: 4 }}>
        <Grid item xs={3}>
          <Box sx={{ width: '75%' }}>{erc20WeiToCommaDecString(poolCTO.totalAssets)} </Box>
        </Grid>
        <Grid item xs={3}>
          <Typography sx={{ marginLeft: 1 }} variant="bodyBold">
            {erc20WeiToCommaDecString(poolCTO.totalShares)}
          </Typography>
        </Grid>
        <Grid item xs={2}>
          <Typography sx={{ marginLeft: 1 }} variant="bodyBold">
            {toUTCDateString(chainTimestamp)}
          </Typography>
        </Grid>
        <Grid item xs={4}>
          <Typography sx={{ marginLeft: 1 }} variant="bodyBold">
            {exchangeRateToDecimalString(poolCTO.exchangeRate)}
          </Typography>
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TableHeaderItem>New Values after exchange rate update</TableHeaderItem>
        </Grid>
      </Grid>
      <Grid container sx={{ marginBottom: 4 }}>
        <Grid item xs={3}>
          <Typography sx={{ marginLeft: 1 }} variant="bodyBold">
            {erc20WeiToCommaDecString(poolCTO.totalShares)}
          </Typography>
        </Grid>
        <Grid item xs={2}>
          <Typography sx={{ marginLeft: 1 }} variant="bodyBold">
            {toUTCDateString(chainTimestamp)}
          </Typography>
        </Grid>
        <Grid item xs={4}>
          <Typography sx={{ marginLeft: 1 }} variant="bodyBold">
            {exchangeRateDecimalString}
          </Typography>
        </Grid>
      </Grid>

      <Grid item xs={12}>
        <InputLabel htmlFor="exchangeRate">ExchangeRate</InputLabel>
        <TextField
          id="exchangeRate"
          inputProps={{
            'data-testid': 'exchangeRate',
          }}
          sx={{ width: '300px' }}
          variant="outlined"
          placeholder="Enter Exchange Rate (Decimal Form)"
          value={exchangeRateDecimalString}
          type={'number'}
          onChange={(event: React.ChangeEvent<HTMLInputElement>): void => {
            setExchangeRateDecimalString(event.target.value)
            setExchangeRate(exchangeRateDecimalStringToExchangeRate(event.target.value).toString())
          }}
          error={!!errorAmount}
          helperText={errorAmount}
        />
      </Grid>
      <Grid item xs={12} sm={12}>
        <Button
          disabled={isSubmitted || disableButton}
          sx={sx}
          onClick={() => {
            setExchangeRateAction()
          }}
          variant={'contained'}
        >
          <Typography variant={'buttonBold'}>Set Exchange rate</Typography>
        </Button>
      </Grid>
      {disableButton && (
        <Typography variant="body" align="left" sx={{ marginBottom: 2 }}>
          Disabled
        </Typography>
      )}
    </Grid>
  )
}
